import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../lib/api';

export type PlanId = 'starter' | 'professional' | 'enterprise' | 'free' | null;

const PLAN_RANK: Record<string, number> = {
  free: 0,
  starter: 1,
  starter_launch: 1,
  professional: 2,
  professional_launch: 2,
  enterprise: 3,
  enterprise_launch: 3,
};

interface TrialInfo {
  isOnTrial: boolean;
  trialEndsAt: string | null;
  daysRemaining: number;
}

interface SubscriptionContextType {
  planId: PlanId;
  planName: string | null;
  status: string | null;
  isLoading: boolean;
  trial: TrialInfo;
  hasPlan: (required: PlanId) => boolean;
  refresh: () => void;
}

const DEFAULT_TRIAL: TrialInfo = { isOnTrial: false, trialEndsAt: null, daysRemaining: 0 };

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({
  tenantId,
  children,
}: {
  tenantId: string;
  children: ReactNode;
}) {
  const { getToken } = useAuth();
  const [planId, setPlanId] = useState<PlanId>(null);
  const [planName, setPlanName] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [trial, setTrial] = useState<TrialInfo>(DEFAULT_TRIAL);
  const [isLoading, setIsLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;

      try {
        const res = await fetch(`${API_BASE_URL}/api/subscriptions/current`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant-ID': tenantId,
            'X-Tenant-Slug': tenantId,
            'x-tenant-id': tenantId,
          },
        });

        if (!res.ok) {
          // If 403, the user may lack admin role but still has a subscription.
          // Treat as no-plan so trial logic can still unlock features.
          console.warn(`Subscription fetch returned ${res.status}`);
          if (!cancelled) setIsLoading(false);
          return;
        }

        const data = await res.json();
        if (cancelled) return;

        const sub = data?.subscription;
        setPlanId((sub?.plan_id as PlanId) ?? null);
        setPlanName(sub?.plan_name ?? null);
        setStatus(sub?.status ?? null);

        if (data?.trial) {
          setTrial({
            isOnTrial: Boolean(data.trial.is_on_trial),
            trialEndsAt: data.trial.trial_ends_at ?? null,
            daysRemaining: Number(data.trial.days_remaining ?? 0),
          });
        }
      } catch {
        // Non-fatal: treat as no plan
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, tick, getToken]);

  // During trial all features are unlocked regardless of plan
  const hasPlan = useCallback(
    (required: PlanId): boolean => {
      if (!required) return true;
      if (trial.isOnTrial) return true;
      const current = PLAN_RANK[planId ?? 'free'] ?? 0;
      const needed = PLAN_RANK[required] ?? 0;
      return current >= needed;
    },
    [planId, trial.isOnTrial],
  );

  return (
    <SubscriptionContext.Provider value={{ planId, planName, status, isLoading, trial, hasPlan, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used inside SubscriptionProvider');
  return ctx;
}
